/**
 * Vedic Chart Geometry, Semantic Zoning & Dynamic Layout Engine
 * 
 * Provides responsive, aspect-ratio-preserving coordinate grids, semantic metadata zoning,
 * analytical bounding box modeling, and deterministic multi-pass collision resolution for
 * North Indian, South Indian, and East Indian astrological charts.
 * 
 * Works identically across Web interactive views, SVG, PNG, Print CSS, and PDF generation
 * without requiring DOM measurement hooks.
 * 
 * Protected Baseline: Pure presentation and layout engine. Zero Jyotish calculations.
 */

import {
  KundaliChartData,
  PlanetPosition,
  AshtakvargaData,
  Language,
  ChartStyle,
  ZodiacSign
} from '../types/kundali';
import { getPlanetShortCode, getZodiacName } from './language';
import { getSavByBhava, getSavBySign, ZODIAC_SIGNS } from './vedicEngine';

export interface Point {
  x: number;
  y: number;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type SemanticZoneRole = 
  | 'sign-num' 
  | 'planet' 
  | 'lagna-badge' 
  | 'sav' 
  | 'house-num';

export interface PlacedElement {
  id: string;
  role: SemanticZoneRole;
  houseNumber: number;
  signIndex: number;
  x: number;
  y: number;
  fontSize: number;
  textAnchor: 'start' | 'middle' | 'end';
  dominantBaseline: 'central' | 'alphabetic' | 'hanging';
  text: string;
  bounds: BoundingBox;
  fillColor?: string;
  metadata?: Record<string, any>;
}

export interface ResolvedPlanetLayout {
  planet: PlanetPosition;
  name: string;
  shortCode: string;
  labelText: string;
  degreeText?: string;
  retroText?: string;
  dignityArrow?: '↑' | '↓';
  x: number;
  y: number;
  fontSize: number;
  bounds: BoundingBox;
  fillColor: string;
}

export interface ResolvedHouseLayout {
  houseNumber: number;
  signIndex: number;
  signNumber: number;
  signLabel: string;
  polygonPoints?: string;
  vertices?: Point[];
  box?: BoundingBox;
  rashiAnchor: {
    x: number;
    y: number;
    fontSize: number;
    text: string;
    bounds: BoundingBox;
  };
  savAnchor?: {
    x: number;
    y: number;
    points: number;
    bounds: BoundingBox;
  };
  lagnaAnchor?: {
    x: number;
    y: number;
    text: string;
    bounds: BoundingBox;
  };
  houseNumAnchor?: {
    x: number;
    y: number;
    fontSize: number;
    text: string;
    bounds: BoundingBox;
  };
  planetBase: Point;
  planets: ResolvedPlanetLayout[];
  elements: PlacedElement[];
}

export interface LayoutDiagnostic {
  type: 'text-overlap' | 'zone-collision' | 'boundary-breach' | 'clearance-violation';
  severity: 'error' | 'warning';
  houseNumber: number;
  elementIdA: string;
  elementIdB?: string;
  overlapAmount?: number;
  message: string;
}

export interface ChartValidationResult {
  passed: boolean;
  totalCollisions: number;
  boundaryViolations: number;
  diagnostics: LayoutDiagnostic[];
}

export interface ResolvedChartLayout {
  chartStyle: ChartStyle;
  viewBox: string;
  width: number;
  height: number;
  houses: ResolvedHouseLayout[];
  validation: ChartValidationResult;
}

export interface ResolveChartLayoutOptions {
  chartStyle?: ChartStyle;
  chartData?: KundaliChartData;
  planets?: PlanetPosition[];
  ashtakvarga?: AshtakvargaData;
  showDegrees?: boolean;
  lang?: Language;
  antiOverlap?: boolean;
  selectedHouseNumber?: number;
  customAscSignIndex?: number;
}

// Legacy backward-compatibility interfaces
export type NormalizedPoint = Point;
export interface HouseGeometry {
  houseNumber: number;
  signNumber: number;
  polygonPoints?: string;
  signAnchor: NormalizedPoint;
  savAnchor: NormalizedPoint;
  planetCenter: NormalizedPoint;
  bounds?: BoundingBox;
}
export interface DynamicPlanetLayout {
  planetIndex: number;
  name: string;
  offsetX: number;
  offsetY: number;
  fontSize: string;
  signIndex?: number;
}

// -------------------------------------------------------------
// SOUTH INDIAN 4x4 GRID SPECIFICATION
// -------------------------------------------------------------
export const SOUTH_INDIAN_SIGN_GRID: Record<number, { col: number; row: number }> = {
  11: { col: 0, row: 0 }, // Pisces (Top Left)
  0:  { col: 1, row: 0 }, // Aries
  1:  { col: 2, row: 0 }, // Taurus
  2:  { col: 3, row: 0 }, // Gemini (Top Right)
  3:  { col: 3, row: 1 }, // Cancer
  4:  { col: 3, row: 2 }, // Leo
  5:  { col: 3, row: 3 }, // Virgo (Bottom Right)
  6:  { col: 2, row: 3 }, // Libra
  7:  { col: 1, row: 3 }, // Scorpio
  8:  { col: 0, row: 3 }, // Sagittarius (Bottom Left)
  9:  { col: 0, row: 2 }, // Capricorn
  10: { col: 0, row: 1 }  // Aquarius
};

// -------------------------------------------------------------
// NORTH INDIAN POLYGON SPECIFICATION (ViewBox 400x400)
// Outer square: 10,10 to 390,390. Center: 200,200.
// -------------------------------------------------------------
export const NORTH_INDIAN_HOUSE_POLYGONS: Record<number, string> = {
  1:  '200,10 295,105 200,200 105,105',
  2:  '10,10 200,10 105,105',
  3:  '10,10 105,105 10,200',
  4:  '10,200 105,105 200,200 105,295',
  5:  '10,200 105,295 10,390',
  6:  '10,390 105,295 200,390',
  7:  '200,200 295,295 200,390 105,295',
  8:  '200,390 295,295 390,390',
  9:  '390,390 295,295 390,200',
  10: '200,200 295,105 390,200 295,295',
  11: '390,200 295,105 390,10',
  12: '200,10 295,105 390,10'
};

// -------------------------------------------------------------
// ANALYTICAL FONT METRICS & BOUNDING BOX MODELER
// -------------------------------------------------------------
export function estimateTextDimensions(
  text: string,
  fontSize: number
): { width: number; height: number } {
  if (!text) return { width: 0, height: 0 };

  // Calculate approximate width by inspecting glyph categories
  let totalWidth = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    // Devanagari Unicode block (0x0900 - 0x097F)
    if (code >= 0x0900 && code <= 0x097F) {
      totalWidth += fontSize * 0.70;
    } else if (text[i] === '°') {
      totalWidth += fontSize * 0.40;
    } else if (text[i] === '(' || text[i] === ')') {
      totalWidth += fontSize * 0.35;
    } else if (text[i] === '↑' || text[i] === '↓') {
      totalWidth += fontSize * 0.90;
    } else if (code >= 65 && code <= 90) { // Uppercase Latin
      totalWidth += fontSize * 0.65;
    } else if (code >= 48 && code <= 57) { // Digits
      totalWidth += fontSize * 0.60;
    } else { // Lowercase Latin / other
      totalWidth += fontSize * 0.52;
    }
  }

  const height = fontSize * 1.15;
  return { width: Math.max(totalWidth, fontSize * 0.6), height };
}

export function computeBoundingBox(
  x: number,
  y: number,
  text: string,
  fontSize: number,
  anchor: 'start' | 'middle' | 'end' = 'middle',
  baseline: 'central' | 'alphabetic' | 'hanging' = 'central'
): BoundingBox {
  const { width, height } = estimateTextDimensions(text, fontSize);
  let bx = x;
  let by = y;

  if (anchor === 'middle') {
    bx = x - width / 2;
  } else if (anchor === 'end') {
    bx = x - width;
  }

  if (baseline === 'central') {
    by = y - height / 2;
  } else if (baseline === 'alphabetic') {
    by = y - height * 0.85;
  } else if (baseline === 'hanging') {
    by = y;
  }

  return { x: bx, y: by, width, height };
}

export function checkAABBCollision(
  boxA: BoundingBox,
  boxB: BoundingBox,
  minClearance: number = 2.0
): { collides: boolean; xOverlap: number; yOverlap: number } {
  const xOverlap = Math.min(boxA.x + boxA.width, boxB.x + boxB.width) - Math.max(boxA.x, boxB.x);
  const yOverlap = Math.min(boxA.y + boxA.height, boxB.y + boxB.height) - Math.max(boxA.y, boxB.y);

  // Consider collision if overlapping or within breathing clearance buffer
  const collides = xOverlap > -minClearance && yOverlap > -minClearance;
  return { collides, xOverlap, yOverlap };
}

// Point-in-polygon test (ray casting)
export function isPointInsidePolygon(pt: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    const intersect = ((yi > pt.y) !== (yj > pt.y)) &&
      (pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export function parsePolygonPoints(polyStr: string): Point[] {
  return polyStr.trim().split(/\s+/).map(pair => {
    const [x, y] = pair.split(',').map(Number);
    return { x, y };
  });
}

// -------------------------------------------------------------
// NORTH INDIAN SEMANTIC ZONING DEFINITIONS
// -------------------------------------------------------------
interface NorthIndianHouseZones {
  rashiAnchor: Point;
  planetBase: Point;
  savAnchor: Point;
  lagnaAnchor?: Point;
}

/**
 * Unified geometric anchors for North Indian houses (viewBox 400x400)
 * Completely isolates structural metadata from the primary planet clusters.
 */
export const NORTH_INDIAN_ZONES: Record<number, NorthIndianHouseZones> = {
  // Kendras (Diamonds with central vertex at 200,200)
  1:  { rashiAnchor: { x: 200, y: 178 }, planetBase: { x: 200, y: 88 },  savAnchor: { x: 200, y: 28 },  lagnaAnchor: { x: 200, y: 145 } },
  4:  { rashiAnchor: { x: 176, y: 200 }, planetBase: { x: 98,  y: 200 }, savAnchor: { x: 28,  y: 200 } },
  7:  { rashiAnchor: { x: 200, y: 226 }, planetBase: { x: 200, y: 305 }, savAnchor: { x: 200, y: 372 } },
  10: { rashiAnchor: { x: 224, y: 200 }, planetBase: { x: 302, y: 200 }, savAnchor: { x: 372, y: 200 } },

  // Horizontal Triangles (Apex pointing towards chart center, wide base outer)
  // House 6 (Regression fixture): Apex at (105, 295) points up; base is at bottom y=390
  2:  { rashiAnchor: { x: 105, y: 84 },  planetBase: { x: 105, y: 46 },  savAnchor: { x: 42,  y: 24 } },
  6:  { rashiAnchor: { x: 105, y: 316 }, planetBase: { x: 105, y: 354 }, savAnchor: { x: 42,  y: 376 } },
  8:  { rashiAnchor: { x: 295, y: 316 }, planetBase: { x: 295, y: 354 }, savAnchor: { x: 358, y: 376 } },
  12: { rashiAnchor: { x: 295, y: 84 },  planetBase: { x: 295, y: 46 },  savAnchor: { x: 358, y: 24 } },

  // Vertical Triangles (Apex pointing towards chart center, wide base outer)
  3:  { rashiAnchor: { x: 84,  y: 105 }, planetBase: { x: 46,  y: 105 }, savAnchor: { x: 24,  y: 42 } },
  5:  { rashiAnchor: { x: 84,  y: 295 }, planetBase: { x: 46,  y: 295 }, savAnchor: { x: 24,  y: 358 } },
  9:  { rashiAnchor: { x: 316, y: 295 }, planetBase: { x: 354, y: 295 }, savAnchor: { x: 376, y: 358 } },
  11: { rashiAnchor: { x: 316, y: 105 }, planetBase: { x: 354, y: 105 }, savAnchor: { x: 376, y: 42 } }
};

// -------------------------------------------------------------
// DYNAMIC PLANET PACKING ALGORITHMS
// -------------------------------------------------------------

/**
 * Calculates adaptive planet offsets for North Indian houses.
 */
export function calculateNorthIndianPlanetPacking(
  totalPlanets: number,
  hNum: number,
  idx: number,
  showDegrees: boolean
): { offsetX: number; offsetY: number; fontSize: number } {
  let offsetX = 0;
  let offsetY = 0;
  // Adaptive font sizing based strictly on local house density
  let fontSize = showDegrees
    ? totalPlanets === 1 ? 12.0 : totalPlanets === 2 ? 11.0 : totalPlanets === 3 ? 10.0 : totalPlanets === 4 ? 9.0 : 8.0
    : totalPlanets === 1 ? 14.0 : totalPlanets === 2 ? 12.5 : totalPlanets === 3 ? 11.0 : totalPlanets === 4 ? 10.0 : 8.8;

  const isKendra = [1, 4, 7, 10].includes(hNum);
  const isHorizontalTriangle = [2, 6, 8, 12].includes(hNum);

  if (hNum === 1) {
    if (totalPlanets === 1) {
      offsetX = 0; offsetY = 0;
    } else if (totalPlanets === 2) {
      offsetX = 0;
      offsetY = idx === 0 ? -14 : 14;
    } else if (totalPlanets === 3) {
      if (idx === 0) { offsetX = -26; offsetY = -12; }
      else if (idx === 1) { offsetX = 26; offsetY = -12; }
      else { offsetX = 0; offsetY = 14; }
    } else if (totalPlanets === 4) {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      offsetX = col === 0 ? -26 : 26;
      offsetY = row === 0 ? -14 : 14;
    } else {
      const cols = 3;
      const row = Math.floor(idx / cols);
      const col = idx % cols;
      offsetX = (col - 1) * 26;
      offsetY = (row - (Math.ceil(totalPlanets / cols) - 1) / 2) * (showDegrees ? 13 : 15);
    }
  } else if (hNum === 7) {
    if (totalPlanets === 1) {
      offsetX = 0; offsetY = 0;
    } else if (totalPlanets === 2) {
      offsetX = 0;
      offsetY = idx === 0 ? -14 : 14;
    } else if (totalPlanets === 3) {
      if (idx === 0) { offsetX = 0; offsetY = -14; }
      else if (idx === 1) { offsetX = -26; offsetY = 12; }
      else { offsetX = 26; offsetY = 12; }
    } else if (totalPlanets === 4) {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      offsetX = col === 0 ? -26 : 26;
      offsetY = row === 0 ? -14 : 14;
    } else {
      const cols = 3;
      const row = Math.floor(idx / cols);
      const col = idx % cols;
      offsetX = (col - 1) * 26;
      offsetY = (row - (Math.ceil(totalPlanets / cols) - 1) / 2) * (showDegrees ? 13 : 15);
    }
  } else if (hNum === 4 || hNum === 10) {
    // Tall vertical diamonds
    if (totalPlanets === 1) {
      offsetX = 0; offsetY = 0;
    } else if (totalPlanets === 2) {
      offsetX = 0;
      offsetY = idx === 0 ? -18 : 18;
    } else if (totalPlanets === 3) {
      offsetX = 0;
      offsetY = (idx - 1) * 24;
    } else if (totalPlanets === 4) {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      offsetX = col === 0 ? -22 : 22;
      offsetY = row === 0 ? -16 : 16;
    } else {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      offsetX = col === 0 ? -22 : 22;
      offsetY = (row - 1) * 20;
    }
  } else if (isHorizontalTriangle) {
    // Houses 2, 6, 8, 12 (Base is horizontal, ample width 140px)
    const isBottomTriangle = hNum === 6 || hNum === 8;
    if (totalPlanets === 1) {
      offsetX = 0; offsetY = 0;
    } else if (totalPlanets === 2) {
      offsetX = 0;
      offsetY = idx === 0 ? -11 : 11;
    } else if (totalPlanets === 3) {
      // Triad: 1 towards apex, 2 along base
      if (isBottomTriangle) {
        if (idx === 0) { offsetX = 0; offsetY = -12; }
        else if (idx === 1) { offsetX = -24; offsetY = 12; }
        else { offsetX = 24; offsetY = 12; }
      } else {
        if (idx === 0) { offsetX = -24; offsetY = -12; }
        else if (idx === 1) { offsetX = 24; offsetY = -12; }
        else { offsetX = 0; offsetY = 12; }
      }
    } else if (totalPlanets === 4) {
      offsetX = 0;
      offsetY = (idx - 1.5) * 13;
      fontSize = showDegrees ? 8.0 : 9.0;
    } else {
      offsetX = 0;
      offsetY = (idx - (totalPlanets - 1) / 2) * (showDegrees ? 11 : 13);
      fontSize = showDegrees ? 7.2 : 8.0;
    }
  } else {
    // Vertical Triangles: Houses 3, 5, 9, 11 (Base is vertical, ample height 140px)
    const isLeftTriangle = hNum === 3 || hNum === 5;
    if (totalPlanets === 1) {
      fontSize = showDegrees ? 10.5 : 12.0;
      offsetX = isLeftTriangle ? -1 : 1;
      offsetY = 0;
    } else if (totalPlanets === 2) {
      fontSize = showDegrees ? 10.0 : 11.0;
      offsetX = isLeftTriangle ? -1 : 1;
      offsetY = idx === 0 ? -18 : 18;
    } else if (totalPlanets === 3) {
      fontSize = showDegrees ? 9.2 : 10.2;
      offsetX = isLeftTriangle ? -1 : 1;
      offsetY = (idx - 1) * 22;
    } else if (totalPlanets === 4) {
      fontSize = showDegrees ? 8.2 : 9.2;
      offsetX = isLeftTriangle ? -1 : 1;
      offsetY = (idx - 1.5) * 18;
    } else {
      fontSize = showDegrees ? 7.5 : 8.2;
      offsetX = isLeftTriangle ? -1 : 1;
      offsetY = (idx - (totalPlanets - 1) / 2) * (showDegrees ? 13 : 15);
    }
  }

  return { offsetX, offsetY, fontSize };
}

/**
 * Calculates adaptive planet positions for South Indian 97.5x97.5 sign boxes.
 */
export function calculateSouthIndianPlanetPacking(
  totalPlanets: number,
  idx: number,
  showDegrees: boolean,
  boxX: number,
  boxY: number
): { x: number; y: number; fontSize: number } {
  const fontSize = showDegrees
    ? totalPlanets === 1 ? 11.5 : totalPlanets === 2 ? 10.5 : totalPlanets <= 4 ? 9.0 : 8.0
    : totalPlanets === 1 ? 13.0 : totalPlanets === 2 ? 11.5 : totalPlanets <= 4 ? 10.2 : 8.8;

  let px = boxX + 48.75;
  let py = boxY + 58;

  if (totalPlanets === 1) {
    px = boxX + 48.75;
    py = boxY + 58;
  } else if (totalPlanets === 2) {
    px = boxX + 48.75;
    py = idx === 0 ? boxY + 46 : boxY + 70;
  } else if (totalPlanets === 3) {
    if (idx === 0) {
      px = boxX + 48.75;
      py = boxY + 44;
    } else if (idx === 1) {
      px = boxX + 28;
      py = boxY + 70;
    } else {
      px = boxX + 70;
      py = boxY + 70;
    }
  } else if (totalPlanets === 4) {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    px = col === 0 ? boxX + 28 : boxX + 70;
    py = row === 0 ? boxY + 44 : boxY + 70;
  } else {
    const cols = totalPlanets <= 6 ? 2 : 3;
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    if (cols === 2) {
      px = col === 0 ? boxX + 26 : boxX + 72;
      py = boxY + 40 + row * (showDegrees ? 13 : 15);
    } else {
      px = boxX + 18 + col * 31;
      py = boxY + 40 + row * (showDegrees ? 13 : 15);
    }
  }

  return { x: px, y: py, fontSize };
}

// -------------------------------------------------------------
// MAIN RESOLVED CHART LAYOUT ENGINE
// -------------------------------------------------------------

/**
 * Master analytical chart layout engine.
 * Generates an immutable, collision-resolved, and verified layout model.
 */
export function resolveChartLayout(options: ResolveChartLayoutOptions): ResolvedChartLayout {
  const {
    chartStyle = 'north',
    chartData,
    planets: propPlanets,
    ashtakvarga,
    showDegrees = false,
    lang = 'en',
    antiOverlap = true,
    customAscSignIndex
  } = options;

  const activePlanets = propPlanets || chartData?.planets || [];
  const ascSignIndex = customAscSignIndex !== undefined 
    ? customAscSignIndex 
    : (chartData?.ascendantIndex ?? 0);

  // Extract Ascendant degree string
  const ascPlanet = activePlanets.find(p => p.name === 'Ascendant');
  const ascDeg = ascPlanet?.degreeInSign ?? null;
  const ascDegInt = ascDeg !== null ? Math.floor(ascDeg) : null;
  const ascMinInt = ascDeg !== null ? Math.floor((ascDeg % 1) * 60) : null;
  const ascDegStr = ascDegInt !== null 
    ? `${ascDegInt}°${ascMinInt !== null && ascMinInt > 0 ? `${String(ascMinInt).padStart(2, '0')}'` : ''}` 
    : '';

  const houses: ResolvedHouseLayout[] = [];
  const diagnostics: LayoutDiagnostic[] = [];
  let totalCollisions = 0;
  let boundaryViolations = 0;

  if (chartStyle === 'north') {
    // -------------------------------------------------------------
    // NORTH INDIAN LAYOUT PIPELINE
    // -------------------------------------------------------------
    for (let hNum = 1; hNum <= 12; hNum++) {
      const signIndex = (ascSignIndex + (hNum - 1)) % 12;
      const signNumber = signIndex + 1;
      const signLabel = getZodiacName(ZODIAC_SIGNS[signIndex], lang);
      const polyStr = NORTH_INDIAN_HOUSE_POLYGONS[hNum];
      const vertices = parsePolygonPoints(polyStr);
      const zones = NORTH_INDIAN_ZONES[hNum];

      // Structural Rashi metadata
      const rashiText = String(signNumber);
      const rashiFontSize = [1, 4, 7, 10].includes(hNum) ? 17 : 15;
      const rashiBounds = computeBoundingBox(
        zones.rashiAnchor.x,
        zones.rashiAnchor.y,
        rashiText,
        rashiFontSize,
        'middle',
        'central'
      );

      // Ashtakavarga SAV metadata badge
      let savAnchorData: ResolvedHouseLayout['savAnchor'] | undefined = undefined;
      const avPts = ashtakvarga ? getSavByBhava(ashtakvarga, hNum, ascSignIndex) : null;
      if (avPts !== null) {
        const savBounds = {
          x: zones.savAnchor.x - 12,
          y: zones.savAnchor.y - 6.5,
          width: 24,
          height: 13
        };
        savAnchorData = {
          x: zones.savAnchor.x,
          y: zones.savAnchor.y,
          points: avPts,
          bounds: savBounds
        };
      }

      // Lagna Badge for House 1
      let lagnaAnchorData: ResolvedHouseLayout['lagnaAnchor'] | undefined = undefined;
      if (hNum === 1 && zones.lagnaAnchor) {
        const lagnaLabel = `${lang === 'hi' ? 'लग्न' : 'ASC'}${showDegrees && ascDegStr ? ` ${ascDegStr}` : ''}`;
        const badgeWidth = showDegrees && ascDegStr ? 64 : 36;
        const lagnaBounds = {
          x: zones.lagnaAnchor.x - badgeWidth / 2,
          y: zones.lagnaAnchor.y - 10,
          width: badgeWidth,
          height: 20
        };
        lagnaAnchorData = {
          x: zones.lagnaAnchor.x,
          y: zones.lagnaAnchor.y,
          text: lagnaLabel,
          bounds: lagnaBounds
        };
      }

      // Extract occupant planets (excluding 'Ascendant' marker in H1 since it's displayed in Lagna badge)
      const rawInHouse = activePlanets.filter(p => p.house === hNum);
      const filteredInHouse = hNum === 1 ? rawInHouse.filter(p => p.name !== 'Ascendant') : rawInHouse;
      
      // Deterministic sort by classical planetary hierarchy to guarantee order-independent rendering
      const PLANET_HIERARCHY: Record<string, number> = {
        Ascendant: 0, Sun: 1, Moon: 2, Mars: 3, Mercury: 4, Jupiter: 5, Venus: 6, Saturn: 7, Rahu: 8, Ketu: 9
      };
      const inHouse = [...filteredInHouse].sort((a, b) => {
        const orderA = PLANET_HIERARCHY[a.name] ?? 99;
        const orderB = PLANET_HIERARCHY[b.name] ?? 99;
        return orderA - orderB || a.name.localeCompare(b.name);
      });

      // Initial analytical placement for occupant planets
      const resolvedPlanets: ResolvedPlanetLayout[] = inHouse.map((p, idx) => {
        const packing = calculateNorthIndianPlanetPacking(inHouse.length, hNum, idx, showDegrees);
        const pCode = getPlanetShortCode(p.name, lang);
        const retroSuffix = p.isRetrograde ? (lang === 'hi' ? '(व)' : '(r)') : '';
        const degInSign = Math.floor(p.degreeInSign ?? 0);
        const degSuffix = `${degInSign}°`;
        const labelText = showDegrees ? `${pCode} ${degSuffix}${retroSuffix}` : `${pCode}${retroSuffix}`;

        const isExalted = p.dignity === 'Exalted';
        const isDebilitated = p.dignity === 'Debilitated';
        const dignityArrow = isExalted ? '↑' : isDebilitated ? '↓' : undefined;
        let fillColor = '#263633';
        if (isExalted) fillColor = '#047857';
        else if (isDebilitated) fillColor = '#dc2626';
        else if (p.dignity === 'Own House') fillColor = '#1F4F4E';
        else if (p.isCombust) fillColor = '#ea580c';

        const px = zones.planetBase.x + packing.offsetX;
        const py = zones.planetBase.y + packing.offsetY;
        const fullRenderedText = dignityArrow ? `${labelText} ${dignityArrow}` : labelText;
        const bounds = computeBoundingBox(px, py, fullRenderedText, packing.fontSize, 'middle', 'central');

        return {
          planet: p,
          name: p.name,
          shortCode: pCode,
          labelText,
          degreeText: degSuffix,
          retroText: retroSuffix,
          dignityArrow,
          x: px,
          y: py,
          fontSize: packing.fontSize,
          bounds,
          fillColor
        };
      });

      // -------------------------------------------------------------
      // DETERMINISTIC MULTI-PASS RELAXATION ENGINE
      // -------------------------------------------------------------
      if (antiOverlap && resolvedPlanets.length >= 1) {
        const MAX_PASSES = 6;
        for (let pass = 0; pass < MAX_PASSES; pass++) {
          let hadOverlap = false;

          // 1. Pairwise Planet-to-Planet collision resolution (if multiple planets)
          if (resolvedPlanets.length > 1) {
            for (let i = 0; i < resolvedPlanets.length; i++) {
              for (let j = i + 1; j < resolvedPlanets.length; j++) {
                const pa = resolvedPlanets[i];
                const pb = resolvedPlanets[j];
                const col = checkAABBCollision(pa.bounds, pb.bounds, 2.5);

                if (col.collides) {
                  hadOverlap = true;
                  // Repel along the axis of smaller overlap with adaptive clearance
                  if (col.yOverlap < col.xOverlap) {
                    const reqDisplacement = col.yOverlap + 2.5 + 0.5;
                    const shift = Math.max(2.0, reqDisplacement / 2);
                    pa.y -= shift;
                    pb.y += shift;
                  } else {
                    const reqDisplacement = col.xOverlap + 2.5 + 0.5;
                    const shift = Math.max(2.0, reqDisplacement / 2);
                    pa.x -= shift;
                    pb.x += shift;
                  }
                  const fullPa = pa.dignityArrow ? `${pa.labelText} ${pa.dignityArrow}` : pa.labelText;
                  const fullPb = pb.dignityArrow ? `${pb.labelText} ${pb.dignityArrow}` : pb.labelText;
                  pa.bounds = computeBoundingBox(pa.x, pa.y, fullPa, pa.fontSize, 'middle', 'central');
                  pb.bounds = computeBoundingBox(pb.x, pb.y, fullPb, pb.fontSize, 'middle', 'central');
                }
              }
            }
          }

          // 2. Planet-to-Rashi and Planet-to-Lagna clearance for ALL planets (even single planet)
          for (let i = 0; i < resolvedPlanets.length; i++) {
            const p = resolvedPlanets[i];
            const rashiCol = checkAABBCollision(p.bounds, rashiBounds, 2.5);
            if (rashiCol.collides) {
              hadOverlap = true;
              const isHorizontal = [2, 6, 8, 12].includes(hNum);
              const isVertical = [3, 5, 9, 11].includes(hNum);
              if (isHorizontal) {
                const signY = p.y >= zones.rashiAnchor.y ? 1 : -1;
                const shiftY = Math.max(3.0, rashiCol.yOverlap + 2.5 + 1.0);
                p.y += signY * shiftY;
              } else if (isVertical) {
                if (Math.abs(p.y - zones.rashiAnchor.y) > 2) {
                  const signY = p.y >= zones.rashiAnchor.y ? 1 : -1;
                  const shiftY = Math.max(2.5, rashiCol.yOverlap + 2.5 + 0.5);
                  p.y += signY * shiftY;
                } else {
                  const signX = p.x >= zones.rashiAnchor.x ? 1 : -1;
                  const shiftX = Math.max(2.5, rashiCol.xOverlap + 2.5 + 0.5);
                  p.x += signX * shiftX;
                }
              } else {
                const dx = p.x - zones.rashiAnchor.x;
                const dy = p.y - zones.rashiAnchor.y;
                const dist = Math.hypot(dx, dy) || 1;
                const shift = Math.max(3.0, Math.max(rashiCol.xOverlap, rashiCol.yOverlap) + 2.5 + 1.0);
                p.x += (dx / dist) * shift;
                p.y += (dy / dist) * shift;
              }
              const fullP = p.dignityArrow ? `${p.labelText} ${p.dignityArrow}` : p.labelText;
              p.bounds = computeBoundingBox(p.x, p.y, fullP, p.fontSize, 'middle', 'central');
              if (p.bounds.x < 11) {
                p.x += (11 - p.bounds.x);
                p.bounds = computeBoundingBox(p.x, p.y, fullP, p.fontSize, 'middle', 'central');
              } else if (p.bounds.x + p.bounds.width > 389) {
                p.x -= (p.bounds.x + p.bounds.width - 389);
                p.bounds = computeBoundingBox(p.x, p.y, fullP, p.fontSize, 'middle', 'central');
              }
            }

            // Check clearance against Lagna Badge
            if (lagnaAnchorData) {
              const lagnaCol = checkAABBCollision(p.bounds, lagnaAnchorData.bounds, 2.5);
              if (lagnaCol.collides) {
                hadOverlap = true;
                const shift = Math.max(3.0, lagnaCol.yOverlap + 2.5 + 1.0);
                p.y -= shift;
                const fullP = p.dignityArrow ? `${p.labelText} ${p.dignityArrow}` : p.labelText;
                p.bounds = computeBoundingBox(p.x, p.y, fullP, p.fontSize, 'middle', 'central');
              }
            }
          }

          if (!hadOverlap) break;
        }
      }

      // Collect placed elements for house
      const houseElements: PlacedElement[] = [
        {
          id: `h${hNum}-sign`,
          role: 'sign-num',
          houseNumber: hNum,
          signIndex,
          x: zones.rashiAnchor.x,
          y: zones.rashiAnchor.y,
          fontSize: rashiFontSize,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: rashiText,
          bounds: rashiBounds
        }
      ];

      if (savAnchorData) {
        houseElements.push({
          id: `h${hNum}-sav`,
          role: 'sav',
          houseNumber: hNum,
          signIndex,
          x: savAnchorData.x,
          y: savAnchorData.y,
          fontSize: 8.5,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: String(savAnchorData.points),
          bounds: savAnchorData.bounds
        });
      }

      if (lagnaAnchorData) {
        houseElements.push({
          id: 'h1-lagna-badge',
          role: 'lagna-badge',
          houseNumber: 1,
          signIndex,
          x: lagnaAnchorData.x,
          y: lagnaAnchorData.y,
          fontSize: 11,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: lagnaAnchorData.text,
          bounds: lagnaAnchorData.bounds
        });
      }

      resolvedPlanets.forEach(p => {
        houseElements.push({
          id: `h${hNum}-p-${p.name}`,
          role: 'planet',
          houseNumber: hNum,
          signIndex,
          x: p.x,
          y: p.y,
          fontSize: p.fontSize,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: p.labelText,
          bounds: p.bounds,
          fillColor: p.fillColor,
          metadata: { planet: p.planet }
        });
      });

      // -------------------------------------------------------------
      // HARD GEOMETRIC VALIDATION FOR HOUSE
      // -------------------------------------------------------------
      for (let i = 0; i < houseElements.length; i++) {
        for (let j = i + 1; j < houseElements.length; j++) {
          const elA = houseElements[i];
          const elB = houseElements[j];
          const col = checkAABBCollision(elA.bounds, elB.bounds, 1.5);
          if (col.collides) {
            totalCollisions++;
            diagnostics.push({
              type: 'text-overlap',
              severity: 'error',
              houseNumber: hNum,
              elementIdA: elA.id,
              elementIdB: elB.id,
              overlapAmount: Math.max(col.xOverlap, col.yOverlap),
              message: `Collision between ${elA.id} and ${elB.id} in House ${hNum}`
            });
          }
        }

        // Validate boundary containment (text center and all 4 corners must be inside house polygon)
        const el = houseElements[i];
        if (el.role === 'planet' && vertices) {
          const centerIn = isPointInsidePolygon({ x: el.x, y: el.y }, vertices);
          const c1 = isPointInsidePolygon({ x: el.bounds.x, y: el.bounds.y }, vertices);
          const c2 = isPointInsidePolygon({ x: el.bounds.x + el.bounds.width, y: el.bounds.y }, vertices);
          const c3 = isPointInsidePolygon({ x: el.bounds.x, y: el.bounds.y + el.bounds.height }, vertices);
          const c4 = isPointInsidePolygon({ x: el.bounds.x + el.bounds.width, y: el.bounds.y + el.bounds.height }, vertices);

          if (!centerIn || !c1 || !c2 || !c3 || !c4) {
            boundaryViolations++;
            diagnostics.push({
              type: 'boundary-breach',
              severity: 'error',
              houseNumber: hNum,
              elementIdA: el.id,
              message: `Planet ${el.id} bounds breach House ${hNum} polygon boundary`
            });
          }
        }
      }

      houses.push({
        houseNumber: hNum,
        signIndex,
        signNumber,
        signLabel,
        polygonPoints: polyStr,
        vertices,
        rashiAnchor: {
          x: zones.rashiAnchor.x,
          y: zones.rashiAnchor.y,
          fontSize: rashiFontSize,
          text: rashiText,
          bounds: rashiBounds
        },
        savAnchor: savAnchorData,
        lagnaAnchor: lagnaAnchorData,
        planetBase: zones.planetBase,
        planets: resolvedPlanets,
        elements: houseElements
      });
    }

  } else if (chartStyle === 'south') {
    // -------------------------------------------------------------
    // SOUTH INDIAN LAYOUT PIPELINE (4x4 Grid, 12 outer boxes)
    // -------------------------------------------------------------
    for (let signIdx = 0; signIdx < 12; signIdx++) {
      const grid = SOUTH_INDIAN_SIGN_GRID[signIdx];
      const boxX = grid.col * 97.5 + 5;
      const boxY = grid.row * 97.5 + 5;
      const boxBounds: BoundingBox = { x: boxX, y: boxY, width: 97.5, height: 97.5 };

      const houseNum = ((signIdx - ascSignIndex + 12) % 12) + 1;
      const signNumber = signIdx + 1;
      const signLabel = getZodiacName(ZODIAC_SIGNS[signIdx], lang);

      // Rashi header text (Row 1: y=11)
      const rashiHeader = `R${signNumber} ${signLabel.slice(0, 3)}`;
      const rashiBounds = computeBoundingBox(boxX + 5, boxY + 11, rashiHeader, 8.5, 'start', 'alphabetic');

      // House number text (Row 2: y=24)
      const hNumText = `[H${houseNum}]`;
      const hNumBounds = computeBoundingBox(boxX + 5, boxY + 24, hNumText, 7.5, 'start', 'alphabetic');

      // Lagna Badge (Row 2: right of [H1], y=18 to 30)
      let lagnaAnchorData: ResolvedHouseLayout['lagnaAnchor'] | undefined = undefined;
      if (houseNum === 1) {
        const lagnaLabel = `${lang === 'hi' ? 'लग्न' : 'ASC'}${showDegrees && ascDegStr ? ` ${ascDegStr}` : ''}`;
        const badgeWidth = showDegrees && ascDegStr ? 52 : 30;
        const lagnaX = boxX + 28 + badgeWidth / 2;
        lagnaAnchorData = {
          x: lagnaX,
          y: boxY + 22,
          text: lagnaLabel,
          bounds: { x: boxX + 28, y: boxY + 15, width: badgeWidth, height: 14 }
        };
      }

      // Ashtakavarga SAV points
      let savAnchorData: ResolvedHouseLayout['savAnchor'] | undefined = undefined;
      const avPts = ashtakvarga ? getSavBySign(ashtakvarga, signIdx) : null;
      if (avPts !== null) {
        savAnchorData = {
          x: boxX + 76,
          y: boxY + 12,
          points: avPts,
          bounds: { x: boxX + 66, y: boxY + 6.5, width: 20, height: 11 }
        };
      }

      // Occupant planets
      const rawInSignPlanets = activePlanets.filter(p => p.signIndex === signIdx);
      const filteredInSignPlanets = houseNum === 1 
        ? rawInSignPlanets.filter(p => p.name !== 'Ascendant') 
        : rawInSignPlanets;
      const PLANET_HIERARCHY: Record<string, number> = {
        Ascendant: 0, Sun: 1, Moon: 2, Mars: 3, Mercury: 4, Jupiter: 5, Venus: 6, Saturn: 7, Rahu: 8, Ketu: 9
      };
      const inSignPlanets = [...filteredInSignPlanets].sort((a, b) => {
        const orderA = PLANET_HIERARCHY[a.name] ?? 99;
        const orderB = PLANET_HIERARCHY[b.name] ?? 99;
        return orderA - orderB || a.name.localeCompare(b.name);
      });

      const resolvedPlanets: ResolvedPlanetLayout[] = inSignPlanets.map((p, idx) => {
        const packing = calculateSouthIndianPlanetPacking(inSignPlanets.length, idx, showDegrees, boxX, boxY);
        const pCode = getPlanetShortCode(p.name, lang);
        const retroSuffix = p.isRetrograde ? (lang === 'hi' ? '(व)' : '(r)') : '';
        const degInSign = Math.floor(p.degreeInSign ?? 0);
        const degSuffix = `${degInSign}°`;
        const labelText = showDegrees ? `${pCode} ${degSuffix}${retroSuffix}` : `${pCode}${retroSuffix}`;

        const isExalted = p.dignity === 'Exalted';
        const isDebilitated = p.dignity === 'Debilitated';
        const dignityArrow = isExalted ? '↑' : isDebilitated ? '↓' : undefined;
        let fillColor = '#263633';
        if (isExalted) fillColor = '#047857';
        else if (isDebilitated) fillColor = '#dc2626';
        else if (p.dignity === 'Own House') fillColor = '#1F4F4E';
        else if (p.isCombust) fillColor = '#ea580c';

        const fullRenderedText = dignityArrow ? `${labelText} ${dignityArrow}` : labelText;
        const bounds = computeBoundingBox(packing.x, packing.y, fullRenderedText, packing.fontSize, 'middle', 'central');

        return {
          planet: p,
          name: p.name,
          shortCode: pCode,
          labelText,
          degreeText: degSuffix,
          retroText: retroSuffix,
          dignityArrow,
          x: packing.x,
          y: packing.y,
          fontSize: packing.fontSize,
          bounds,
          fillColor
        };
      });

      // Relaxation for dense South Indian sign boxes
      if (antiOverlap && resolvedPlanets.length >= 1) {
        for (let pass = 0; pass < 4; pass++) {
          let hadOverlap = false;
          if (resolvedPlanets.length > 1) {
            for (let i = 0; i < resolvedPlanets.length; i++) {
              for (let j = i + 1; j < resolvedPlanets.length; j++) {
                const pa = resolvedPlanets[i];
                const pb = resolvedPlanets[j];
                const col = checkAABBCollision(pa.bounds, pb.bounds, 2.0);
                if (col.collides) {
                  hadOverlap = true;
                  if (col.yOverlap < col.xOverlap) {
                    const shift = Math.max(1.5, (col.yOverlap + 2.0 + 0.5) / 2);
                    pa.y -= shift;
                    pb.y += shift;
                  } else {
                    const shift = Math.max(2.0, (col.xOverlap + 2.0 + 0.5) / 2);
                    pa.x -= shift;
                    pb.x += shift;
                  }
                  const fullPa = pa.dignityArrow ? `${pa.labelText} ${pa.dignityArrow}` : pa.labelText;
                  const fullPb = pb.dignityArrow ? `${pb.labelText} ${pb.dignityArrow}` : pb.labelText;
                  pa.bounds = computeBoundingBox(pa.x, pa.y, fullPa, pa.fontSize, 'middle', 'central');
                  pb.bounds = computeBoundingBox(pb.x, pb.y, fullPb, pb.fontSize, 'middle', 'central');
                }
              }
            }
          }
          if (!hadOverlap) break;
        }
      }

      const houseElements: PlacedElement[] = [
        {
          id: `si-sign-${signIdx}`,
          role: 'sign-num',
          houseNumber: houseNum,
          signIndex: signIdx,
          x: boxX + 5,
          y: boxY + 13,
          fontSize: 8.5,
          textAnchor: 'start',
          dominantBaseline: 'alphabetic',
          text: rashiHeader,
          bounds: rashiBounds
        },
        {
          id: `si-hnum-${signIdx}`,
          role: 'house-num',
          houseNumber: houseNum,
          signIndex: signIdx,
          x: boxX + 5,
          y: boxY + 24,
          fontSize: 7.5,
          textAnchor: 'start',
          dominantBaseline: 'alphabetic',
          text: hNumText,
          bounds: hNumBounds
        }
      ];

      if (savAnchorData) {
        houseElements.push({
          id: `si-sav-${signIdx}`,
          role: 'sav',
          houseNumber: houseNum,
          signIndex: signIdx,
          x: savAnchorData.x,
          y: savAnchorData.y,
          fontSize: 7.5,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: String(savAnchorData.points),
          bounds: savAnchorData.bounds
        });
      }

      if (lagnaAnchorData) {
        houseElements.push({
          id: `si-lagna-${signIdx}`,
          role: 'lagna-badge',
          houseNumber: 1,
          signIndex: signIdx,
          x: lagnaAnchorData.x,
          y: lagnaAnchorData.y,
          fontSize: 7.5,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: lagnaAnchorData.text,
          bounds: lagnaAnchorData.bounds
        });
      }

      resolvedPlanets.forEach(p => {
        houseElements.push({
          id: `si-p-${p.name}`,
          role: 'planet',
          houseNumber: houseNum,
          signIndex: signIdx,
          x: p.x,
          y: p.y,
          fontSize: p.fontSize,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: p.labelText,
          bounds: p.bounds,
          fillColor: p.fillColor,
          metadata: { planet: p.planet }
        });
      });

      // Validation check for sign box
      for (let i = 0; i < houseElements.length; i++) {
        for (let j = i + 1; j < houseElements.length; j++) {
          const col = checkAABBCollision(houseElements[i].bounds, houseElements[j].bounds, 1.5);
          if (col.collides) {
            totalCollisions++;
            diagnostics.push({
              type: 'text-overlap',
              severity: 'error',
              houseNumber: houseNum,
              elementIdA: houseElements[i].id,
              elementIdB: houseElements[j].id,
              message: `Collision in South Indian cell ${signIdx} [H${houseNum}]`
            });
          }
        }
      }

      houses.push({
        houseNumber: houseNum,
        signIndex: signIdx,
        signNumber,
        signLabel,
        box: boxBounds,
        rashiAnchor: {
          x: boxX + 5,
          y: boxY + 13,
          fontSize: 8.5,
          text: rashiHeader,
          bounds: rashiBounds
        },
        savAnchor: savAnchorData,
        lagnaAnchor: lagnaAnchorData,
        houseNumAnchor: {
          x: boxX + 5,
          y: boxY + 24,
          fontSize: 7.5,
          text: hNumText,
          bounds: hNumBounds
        },
        planetBase: { x: boxX + 48.75, y: boxY + 58 },
        planets: resolvedPlanets,
        elements: houseElements
      });
    }

  } else {
    // -------------------------------------------------------------
    // EAST INDIAN LAYOUT PIPELINE
    // -------------------------------------------------------------
    for (let hNum = 1; hNum <= 12; hNum++) {
      const signIndex = (ascSignIndex + (hNum - 1)) % 12;
      const signNumber = signIndex + 1;
      const signLabel = getZodiacName(ZODIAC_SIGNS[signIndex], lang);

      const baseAngle = (hNum * 30 - 15) * (Math.PI / 180);
      const baseX = 200 + Math.cos(baseAngle) * 105;
      const baseY = 200 + Math.sin(baseAngle) * 105;

      const rashiText = String(signNumber);
      const rashiBounds = computeBoundingBox(baseX, baseY - 12, rashiText, 11, 'middle', 'central');

      const rawInHouse = activePlanets.filter(p => p.house === hNum);
      const filteredInHouse = hNum === 1 ? rawInHouse.filter(p => p.name !== 'Ascendant') : rawInHouse;
      const PLANET_HIERARCHY: Record<string, number> = {
        Ascendant: 0, Sun: 1, Moon: 2, Mars: 3, Mercury: 4, Jupiter: 5, Venus: 6, Saturn: 7, Rahu: 8, Ketu: 9
      };
      const inHouse = [...filteredInHouse].sort((a, b) => {
        const orderA = PLANET_HIERARCHY[a.name] ?? 99;
        const orderB = PLANET_HIERARCHY[b.name] ?? 99;
        return orderA - orderB || a.name.localeCompare(b.name);
      });

      const totalInHouse = inHouse.length;
      const resolvedPlanets: ResolvedPlanetLayout[] = inHouse.map((p, idx) => {
        const pCode = getPlanetShortCode(p.name, lang);
        const retroSuffix = p.isRetrograde ? (lang === 'hi' ? '(व)' : '(r)') : '';
        const degInSign = Math.floor(p.degreeInSign ?? 0);
        const degSuffix = `${degInSign}°`;
        const labelText = showDegrees ? `${pCode} ${degSuffix}${retroSuffix}` : `${pCode}${retroSuffix}`;

        const isExalted = p.dignity === 'Exalted';
        const isDebilitated = p.dignity === 'Debilitated';
        const dignityArrow = isExalted ? '↑' : isDebilitated ? '↓' : undefined;

        let fontSize = showDegrees
          ? totalInHouse === 1 ? 9.5 : totalInHouse === 2 ? 8.5 : totalInHouse === 3 ? 7.8 : totalInHouse === 4 ? 7.0 : 6.5
          : totalInHouse === 1 ? 10.5 : totalInHouse === 2 ? 9.5 : totalInHouse === 3 ? 8.5 : totalInHouse === 4 ? 7.8 : 7.0;

        let px = baseX;
        let py = baseY + 8;
        if (totalInHouse === 1) {
          px = baseX;
          py = baseY + 8;
        } else if (totalInHouse === 2) {
          px = baseX;
          py = baseY + (idx === 0 ? 3 : 16);
        } else if (totalInHouse === 3) {
          px = baseX;
          py = baseY + (idx === 0 ? 3 : idx === 1 ? 14 : 25);
        } else if (totalInHouse === 4) {
          px = baseX;
          py = baseY + 1 + idx * 10.0;
        } else {
          px = baseX;
          py = baseY + 1 + idx * 9.2;
        }

        const fullRenderedText = dignityArrow ? `${labelText} ${dignityArrow}` : labelText;
        const bounds = computeBoundingBox(px, py, fullRenderedText, fontSize, 'middle', 'central');

        return {
          planet: p,
          name: p.name,
          shortCode: pCode,
          labelText,
          degreeText: degSuffix,
          retroText: retroSuffix,
          dignityArrow,
          x: px,
          y: py,
          fontSize,
          bounds,
          fillColor: '#1F4F4E'
        };
      });

      // Relaxation for East Indian houses
      if (antiOverlap && resolvedPlanets.length >= 1) {
        for (let pass = 0; pass < 4; pass++) {
          let hadOverlap = false;
          if (resolvedPlanets.length > 1) {
            for (let i = 0; i < resolvedPlanets.length; i++) {
              for (let j = i + 1; j < resolvedPlanets.length; j++) {
                const pa = resolvedPlanets[i];
                const pb = resolvedPlanets[j];
                const col = checkAABBCollision(pa.bounds, pb.bounds, 1.5);
                if (col.collides) {
                  hadOverlap = true;
                  const shift = Math.max(1.5, (col.yOverlap + 2.0) / 2);
                  pa.y -= shift;
                  pb.y += shift;
                  const fullPa = pa.dignityArrow ? `${pa.labelText} ${pa.dignityArrow}` : pa.labelText;
                  const fullPb = pb.dignityArrow ? `${pb.labelText} ${pb.dignityArrow}` : pb.labelText;
                  pa.bounds = computeBoundingBox(pa.x, pa.y, fullPa, pa.fontSize, 'middle', 'central');
                  pb.bounds = computeBoundingBox(pb.x, pb.y, fullPb, pb.fontSize, 'middle', 'central');
                }
              }
            }
          }
          for (let i = 0; i < resolvedPlanets.length; i++) {
            const p = resolvedPlanets[i];
            const rCol = checkAABBCollision(p.bounds, rashiBounds, 1.5);
            if (rCol.collides) {
              hadOverlap = true;
              p.y += Math.max(2.0, rCol.yOverlap + 2.0);
              const fullP = p.dignityArrow ? `${p.labelText} ${p.dignityArrow}` : p.labelText;
              p.bounds = computeBoundingBox(p.x, p.y, fullP, p.fontSize, 'middle', 'central');
            }
          }
          if (!hadOverlap) break;
        }
      }

      const houseElements: PlacedElement[] = [
        {
          id: `east-sign-${hNum}`,
          role: 'sign-num',
          houseNumber: hNum,
          signIndex,
          x: baseX,
          y: baseY - 12,
          fontSize: 11,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: rashiText,
          bounds: rashiBounds
        }
      ];

      resolvedPlanets.forEach(p => {
        houseElements.push({
          id: `east-p-${p.name}`,
          role: 'planet',
          houseNumber: hNum,
          signIndex,
          x: p.x,
          y: p.y,
          fontSize: p.fontSize,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          text: p.labelText,
          bounds: p.bounds,
          fillColor: p.fillColor
        });
      });

      // Hard geometric validation for East Indian house
      for (let i = 0; i < houseElements.length; i++) {
        for (let j = i + 1; j < houseElements.length; j++) {
          const col = checkAABBCollision(houseElements[i].bounds, houseElements[j].bounds, 1.5);
          if (col.collides) {
            totalCollisions++;
            diagnostics.push({
              type: 'text-overlap',
              severity: 'error',
              houseNumber: hNum,
              elementIdA: houseElements[i].id,
              elementIdB: houseElements[j].id,
              message: `Collision between ${houseElements[i].id} and ${houseElements[j].id} in East Indian House ${hNum}`
            });
          }
        }
      }

      houses.push({
        houseNumber: hNum,
        signIndex,
        signNumber,
        signLabel,
        rashiAnchor: {
          x: baseX,
          y: baseY - 12,
          fontSize: 11,
          text: rashiText,
          bounds: rashiBounds
        },
        planetBase: { x: baseX, y: baseY + 6 },
        planets: resolvedPlanets,
        elements: houseElements
      });
    }
  }

  const passed = totalCollisions === 0 && boundaryViolations === 0;

  return {
    chartStyle,
    viewBox: '0 0 400 400',
    width: 400,
    height: 400,
    houses,
    validation: {
      passed,
      totalCollisions,
      boundaryViolations,
      diagnostics
    }
  };
}

// -------------------------------------------------------------
// LEGACY COMPATIBILITY EXPORTS
// -------------------------------------------------------------
export function getNorthIndianPlanetLayout(
  totalPlanets: number,
  houseNumber: number,
  planetIndex: number,
  showDegrees: boolean
): { fontSize: string; offsetX: number; offsetY: number } {
  const p = calculateNorthIndianPlanetPacking(totalPlanets, houseNumber, planetIndex, showDegrees);
  return {
    fontSize: String(p.fontSize),
    offsetX: p.offsetX,
    offsetY: p.offsetY
  };
}

export function getSouthIndianPlanetLayout(
  totalPlanets: number,
  planetIndex: number,
  showDegrees: boolean,
  boxX: number,
  boxY: number
): { px: number; py: number; fontSize: string } {
  const p = calculateSouthIndianPlanetPacking(totalPlanets, planetIndex, showDegrees, boxX, boxY);
  return {
    px: p.x,
    py: p.y,
    fontSize: String(p.fontSize)
  };
}
